import React, { useEffect, useMemo, useState } from "react";

import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";

import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import { AxiosError } from "axios";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

import { notify } from "../../utils/toast";

import {
  useCreateStationeryRequest,
  useGetAllStationeryProduct,
} from "../../query/HrmQuery";

import {
  ICreateStationeryRequest,
  IQueryStationeryProduct,
  IStationeryProduct,
} from "../../models/Hrm";

const { Text, Title, Paragraph } = Typography;

interface Props {
  stationeryId: number;
}

interface StationerySelectProps {
  value?: IStationeryProduct;
  onChange?: (value: IStationeryProduct) => void;
  placeholder?: string;
  className?: string;
}

const StationerySelect: React.FC<StationerySelectProps> = ({
  value,
  onChange,
  ...props
}) => {
  const [filters, setFilters] = useState<IQueryStationeryProduct>({
    page: 1,
    limit: 10,
  });

  const { data, isFetching } = useGetAllStationeryProduct(filters);

  const [options, setOptions] = useState<any[]>([]);

  useEffect(() => {
    if (data?.data) {
      if (filters.page === 1) {
        setOptions(data.data);
      } else {
        setOptions((prev) => {
          const merged = [...prev];
          data.data.forEach((item: any) => {
            if (!merged.some((x) => x.id === item.id)) {
              merged.push(item);
            }
          });

          return merged;
        });
      }
    }
  }, [data?.data, filters.page]);

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setFilters({
          page: 1,
          limit: 10,
          name: keyword || undefined,
        });
      }, 300),
    [],
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;

    const isBottom =
      Math.round(target.scrollTop + target.offsetHeight) >=
      target.scrollHeight - 10;

    if (isBottom && !isFetching && data?.pagination) {
      const current = (filters.page || 1) * (filters.limit || 10);

      if (current < data.pagination.total) {
        setFilters((prev) => ({
          ...prev,
          page: (prev.page || 1) + 1,
        }));
      }
    }
  };

  return (
    <Select
      {...props}
      showSearch
      filterOption={false}
      value={value?.id}
      onSearch={debouncedSearch}
      onPopupScroll={handleScroll}
      notFoundContent={isFetching ? <Spin size="small" /> : "Không có dữ liệu"}
      onChange={(id) => {
        const selected = options.find((item) => item.id === id);

        if (selected && onChange) {
          onChange(selected);
        }
      }}
      dropdownRender={(menu) => (
        <>
          {menu}

          {isFetching && options.length > 0 && (
            <Flex justify="center" className="py-2">
              <Spin size="small" />
            </Flex>
          )}
        </>
      )}
      options={options.map((opt) => ({
        value: opt.id,
        label: `${opt.name} (${opt.unit})`,
      }))}
    />
  );
};

const PersonalStationeryRequest: React.FC<Props> = ({ stationeryId }) => {
  const [form] = Form.useForm<ICreateStationeryRequest>();

  const createMutation = useCreateStationeryRequest();

  const navigate = useNavigate();

  useEffect(() => {
    form.setFieldsValue({
      items: [],
    });
  }, [form]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        periodId: stationeryId,
        note: values.note,
        items: values.items.map((item: any) => ({
          productId: item.product.id,
          quantity: item.quantity,
          note: item.note,
        })),
      };
      await createMutation.mutateAsync({
        ...payload,
      });
      message.success("Tạo yêu cầu thành công");
      form.resetFields();
      form.setFieldsValue({
        items: [],
      });
      navigate(0);
    } catch (error: unknown) {
      const err = error as AxiosError<{
        message: string;
      }>;
      const msg = err.response?.data?.message || "Tạo yêu cầu thất bại";
      notify.error(msg);
    }
  };

  return (
    <Flex vertical className="px-3 gap-2 w-full">
      <Space>
        <Button
          type="default"
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Quay lại
        </Button>
      </Space>

      <Card className="w-full md:w-4/5 lg:w-2/3 self-center">
        <Space
          direction="vertical"
          size={4}
          style={{
            marginBottom: 20,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Tạo yêu cầu VPP
          </Title>

          <Paragraph type="secondary" style={{ margin: 0 }}>
            Nhập danh sách vật phẩm cần mua
          </Paragraph>
        </Space>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="note"
            label={
              <Space>
                <FileTextOutlined />
                <Text strong>Ghi chú</Text>
              </Space>
            }
          >
            <Input.TextArea rows={2} placeholder="Nhập ghi chú..." />
          </Form.Item>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                <Row
                  gutter={[8, 8]}
                  className="hidden sm:flex mb-2 font-semibold"
                >
                  <Col sm={10}>Tên vật phẩm</Col>
                  <Col sm={5}>Số lượng</Col>
                  <Col sm={7}>Ghi chú</Col>
                  <Col sm={2}></Col>
                </Row>
                {fields.map((field) => (
                  <Row
                    gutter={[8, 8]}
                    key={field.key}
                    align="middle"
                    className="mb-2"
                  >
                    <Col xs={24} sm={10}>
                      <Form.Item
                        {...field}
                        name={[field.name, "product"]}
                        rules={[
                          {
                            required: true,
                            message: "Chọn vật phẩm",
                          },
                        ]}
                        className="m-0"
                      >
                        <StationerySelect
                          placeholder="Chọn vật phẩm"
                          className="w-full"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={5}>
                      <Form.Item
                        {...field}
                        name={[field.name, "quantity"]}
                        rules={[
                          {
                            required: true,
                            message: "Nhập số lượng",
                          },
                        ]}
                        className="m-0"
                      >
                        <InputNumber
                          min={1}
                          className="w-full"
                          placeholder="SL"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "note"]}
                        className="m-0"
                      >
                        <Input
                          className="w-full"
                          placeholder="Ghi chú (tùy chọn)"
                        />
                      </Form.Item>
                    </Col>

                    <Col
                      xs={24}
                      sm={2}
                      className="flex justify-end sm:justify-center"
                    >
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                ))}

                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                >
                  Thêm vật phẩm
                </Button>
              </>
            )}
          </Form.List>

          <div className="mt-6 text-right">
            <Button
              type="primary"
              htmlType="submit"
              loading={createMutation.isPending}
            >
              Gửi yêu cầu
            </Button>
          </div>
        </Form>
      </Card>
    </Flex>
  );
};
export default PersonalStationeryRequest;
